import * as bd from 'react-basic-design';
import { TProvider } from '../../i18n';
import { SectionTitle } from '../../components/ui/section-title';
import SvgAdd from '../../assets/icons/Add';
import { T, translate } from '../../components/basic/text';
import { ManageUserModal } from './manage-user-modal';
import { UserDTO, useSearchUsersApi } from '../../api/users-api';
import { useEffect, useMemo, useState } from 'react';
import { IBasicTableColumn } from '../../interfaces/interfaces';
import { Loading } from '../../components/basic/loading';
import { InitFailed } from '../../components/basic/init-failed';
import { notify } from '../../components/basic/notify';
import { DateCell } from '../../components/table/table-cells';
import classNames from 'classnames';
import { CellChip } from '../../components/basic/cell-chip';

export const Users = () => {
    const [handleUserModal, setHandleUserModal] = useState<{ show: boolean; row?: UserDTO }>({
        show: false,
    });
    const searchUsersApi = useSearchUsersApi();

    const columns: IBasicTableColumn<UserDTO>[] = useMemo(
        () => [
            {
                Header: <T>user-name</T>,
                name: 'userName',
                width: 200,
                Cell: ({ row }) => (
                    <bd.Flex align="center" content="start" gap={2}>
                        <span
                            className={classNames({
                                'text-link': searchUsersApi.result?.allowEdit,
                            })}
                            onClick={() =>
                                searchUsersApi.result?.allowEdit &&
                                setHandleUserModal({ show: true, row })
                            }
                        >
                            {row.userName}
                        </span>
                        {row.isDeleted && <CellChip color="red" label={translate('deleted')} />}
                        {row.isDisabled && <CellChip label={translate('disabled')} />}
                        {row.isServiceUser && (
                            <CellChip color="info" label={translate('service')} />
                        )}
                        {!row.isServiceUser && !row.isDeleted && !row.isDisabled && (
                            <CellChip color="success" label="active" />
                        )}
                    </bd.Flex>
                ),
            },
            {
                Header: <T>display-name</T>,
                name: 'displayName',
                Cell: ({ row }) => <div>{row.displayName}</div>,
            },

            {
                Header: <T>phone-number</T>,
                name: 'phoneNumber',
                Cell: ({ row }) => <div>{row.phoneNumber}</div>,
                width: 120,
            },

            {
                Header: <T>last-update</T>,
                name: 'lastUpdate',
                width: 100,
                Cell: ({ row }) => <DateCell value={row.lastUpdate} />,
            },
            {
                Header: <T>created-at</T>,
                name: 'createdAt',
                width: 100,
                Cell: ({ row }) => <DateCell value={row.createdAt} />,
            },
        ],
        [searchUsersApi.result]
    );

    useEffect(() => {
        if (searchUsersApi.initialized || searchUsersApi.initializing) return;
        searchUsersApi.post(null, {});
    }, []);

    if (searchUsersApi.initializing) return <Loading />;
    if (searchUsersApi.error)
        return (
            <InitFailed ex={searchUsersApi.error} onRetry={() => searchUsersApi.post(null, {})} />
        );

    return (
        <TProvider>
            <div className="p-2">
                <SectionTitle
                    title="users-list"
                    hideBorder
                    controls={
                        <bd.Button
                            className="min-w-80"
                            size="sm"
                            variant="outline"
                            disabled={!searchUsersApi.result?.allowEdit}
                            onClick={() => setHandleUserModal({ show: true })}
                        >
                            <SvgAdd />
                            <T>new-user</T>
                        </bd.Button>
                    }
                />
                <bd.BasicTable
                    className="mt-2 mx-n2 mb-n2"
                    columns={columns}
                    data={searchUsersApi.result?.list}
                    cellCSS={({ row }) => (row.isDisabled ? 'bg-gray-5 text-muted opacity-50' : '')}
                    hover
                />
                <ManageUserModal
                    show={handleUserModal.show}
                    user={handleUserModal.row}
                    allowEdit={!!searchUsersApi.result?.allowEdit}
                    onHide={() => setHandleUserModal({ show: false, row: undefined })}
                    onSave={user => {
                        if (!searchUsersApi.result) return;
                        const updateMode =
                            searchUsersApi.result &&
                            !!searchUsersApi.result.list.length &&
                            handleUserModal.row;
                        searchUsersApi.updateResult({
                            list: updateMode
                                ? searchUsersApi.result.list.map(x =>
                                      x.userName === user.userName ? user : x
                                  )
                                : [...searchUsersApi.result.list, user],
                            allowEdit: searchUsersApi.result.allowEdit,
                        });
                        setHandleUserModal({ show: false, row: undefined });
                        notify.success(
                            translate(handleUserModal.row ? 'user-updated' : 'new-user-created')
                        );
                    }}
                    onDeleted={() => {
                        if (!handleUserModal.row || !searchUsersApi.result) return;
                        const user = handleUserModal.row;
                        searchUsersApi.updateResult({
                            list: searchUsersApi.result.list.map(x =>
                                x.userName === user.userName ? { ...x, isDeleted: true } : x
                            ),
                            allowEdit: searchUsersApi.result.allowEdit,
                        });
                        notify.dark(translate('user-deleted'));
                        setHandleUserModal({ show: false, row: undefined });
                    }}
                />
            </div>
        </TProvider>
    );
};
